import { Component, type OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Process } from '../../../models/engineering/process';
import { LabDip, ParameterAndValues, ProcessIdWithStepValues, StepWithParameters } from '../../../models/lab-dip/lab-dip';
import { Parameter } from '../../../models/engineering/parameter';
import { ActivatedRoute, Router } from '@angular/router';
import { ProcessProxyService } from '../../../proxies/item/process.proxy.service';
import { StepProxyService } from '../../../proxies/item/step.proxy.service';
import { forkJoin, of } from 'rxjs';
import { ParameterProxyService } from '../../../proxies/item/parameter.proxy.service';
import { LabDipRequestProxyService } from '../../../proxies/item/lab-dip-request.proxy.service';
import { LabDipRequest } from '../../../models/lab-dip/lab-dip-request';
import { LabDipProxyService } from '../../../proxies/item/lab-dip.proxy.service';
import { Step } from '../../../models/engineering/step';
import _ from 'lodash';
import { InputParameter } from '../../../models/engineering/input-parameter';
import { ParameterValuesModalComponent } from '../../modals/step-values-modal/parameter-values-modal.component';
import { BreadcrumbService, ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { ICardColor } from '../../../models/lab-dip/card-color.interface';
import { getParameterTypeErrorMessage, parameterValueTypeValidator } from '../../../validators/parameter-type.validator';
import { TranslateService } from '@ngx-translate/core';
import { LapDipRoles } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-lab-dip-details',
  templateUrl: './lab-dip-details.component.html',
  styleUrl: './lab-dip-details.component.scss',
})
export class LabDipDetailsComponent implements OnInit {
  public allParameters: Parameter[] = [];
  public allProcesses: Process[] = [];
  public allSteps: Step[] = [];
  public filteredProcesses: Process[] = [];
  public isEditing = false;
  public isLoading = 0;
  public labDip: LabDip | null = null;
  public labDipRequest: LabDipRequest | null = null;
  public showInstructions = false;
  public showSortProcessesInput = false;
  public colorDataInfo: ICardColor | null = null;

  //

  // handle Form Processes
  public processToAdd = new FormControl<Process | null>(null, Validators.required);
  public name = new FormControl<string>('');
  public status = new FormControl(0);
  processes: ProcessIdWithStepValues[] = [];
  parameters: Parameter[] = [];
  formGroup = new FormGroup({
    name: this.name,
    status: this.status,
  });

  constructor(
    private labDipProxyService: LabDipProxyService,
    private labDipRequestProxyService: LabDipRequestProxyService,
    private parameterProxyService: ParameterProxyService,
    private processProxyService: ProcessProxyService,
    private stepProxyService: StepProxyService,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private router: Router,
    private translateService: TranslateService,
    private breadcrumbService: BreadcrumbService,
  ) {}

  canEdit() {
    // if Admin or Editor
    if (AuthService.hasRole(LapDipRoles.LabDipAdmin) || AuthService.hasRole(LapDipRoles.LabDipEditor)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(LapDipRoles.LabDipViewer)) {
      return false;
    }
    // Default
    return false;
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const id = params['id'];
      if (!id) {
        this.router.navigate(['/lab-dips']);
      }
      if (id !== 'new') {
        this.isLoading++;
        this.isLoading++;
        this.isEditing = true;
        this._getInitialData(id);
      } else {
        this.breadcrumbService.setCurrent('Common_New');
      }
    });
  }

  private _getInitialData(paramId: string): void {
    forkJoin({
      parameters: this.parameterProxyService.getParameters(),
    }).subscribe({
      next: ({ parameters }) => {
        this.allParameters = parameters.data || [];

        this.labDipProxyService.getLabDipById(paramId).subscribe({
          next: (response) => {
            if (response === null) {
              this.router.navigate(['/lab-dips']);
            } else {
              this.labDip = response.data;
              this.breadcrumbService.setCurrent(this.labDip?.labDipNumber);
              this.name.setValue(response.data?.name || '');
              this.status.setValue(response?.data!.status);
              response.data!.processRefWithStepValues.forEach((processIdWithValues) =>
                this.addProcess(processIdWithValues.processId, processIdWithValues),
              );
            }
            this.isLoading--;
          },
        });

        this.labDipRequestProxyService.getLabDipRequestByLabDipId(paramId).subscribe({
          next: (response) => {
            if (response === null) {
              console.log('Failed to fetch necessary data');
            } else {
              this.colorDataInfo = this.parseColorInformation(response.data!);
              this.labDipRequest = response.data;
            }
            this.isLoading--;
          },
          error: (error) => {
            //alert('Failed to fetch necessary data. Please refresh the page.');
            console.error('Failed to fetch necessary data', error);
            this.isLoading--;
          },
        });

        this.isLoading--;
      },
      error: (error) => {
        //alert('Failed to fetch necessary data. Please refresh the page.');
        console.error('Failed to fetch necessary data', error);
        this.isLoading--;
      },
    });

    //Disable form by Rol
    if (!this.canEdit()) {
      this.name.disable();
      this.formGroup.disable();
    }
  }
  public addProcess(processId: number, processIdWithStepValues?: ProcessIdWithStepValues): void {
    const process = this.allProcesses.find((p) => p.processId === processId);
    if (!process) {
      alert(this.translateService.instant('LabDip_ProcessNotFound'));
      return;
    }

    const processIdWithStepVals: ProcessIdWithStepValues = {
      processId: process.processId,
      processParameters: process.additionalParameters
        .map((param) => {
          if (param.isActive) {
            const existingProcessParameter = processIdWithStepValues?.processParameters.find(
              (pm) => pm.parameterRef === param.parameterRef,
            );
            const existingValues = existingProcessParameter?.values;
            const defaultValues = param.values;
            return {
              parameterRef: param.parameterRef,
              parameter: this.allParameters.find((p) => p.id === param.parameterRef),
              values: this.getFinalValues(existingValues, defaultValues),
            };
          }
          return null;
        })
        .filter((p) => p !== null),
      stepWithParameters: process.stepIdsWithOrder.flatMap((step) => {
        const materializedStep = this.allSteps.find((s) => s.stepId === step.stepId);
        if (!materializedStep) return [];
        const stepWithParameters: StepWithParameters = {
          excluded: processIdWithStepValues?.stepWithParameters.find((swp) => swp.stepId === materializedStep.stepId)?.excluded || false,
          stepId: materializedStep.stepId,
          step: materializedStep,
          parameterAndValues: materializedStep.additionalParameters
            .flatMap((param) => {
              if (param.isActive) {
                const existingStepParam = processIdWithStepValues?.stepWithParameters
                  .find((swp) => swp.stepId === materializedStep.stepId)
                  ?.parameterAndValues.find((pav) => pav.parameterRef === param.parameterRef);
                const existingValues = existingStepParam?.values;
                const defaultValues = param.values;
                return {
                  parameterRef: param.parameterRef,
                  parameter: this.parameters.find((p) => p.id === param.parameterRef),
                  values: this.getFinalValues(existingValues, defaultValues),
                };
              }
              return null;
            })
            .filter((p) => p !== null),
        };
        return stepWithParameters;
      }),
      isOpen: false,
    };

    this.processToAdd.setValue(null);
    this.processes.push(processIdWithStepVals);
  }
  public cancel() {
    this.router.navigate(['/lab-dips']);
  }
  public cleanUpEmptySingleValueArrays(labDip: LabDip): void {
    labDip.processRefWithStepValues.forEach((processRef) => {
      processRef.processParameters.forEach((param) => {
        if (param.values.length === 1 && param.values[0] === '') {
          param.values = [];
        }
      });
      processRef.stepWithParameters.forEach((step) => {
        step.parameterAndValues.forEach((param) => {
          if (param.values.length === 1 && param.values[0] === '') {
            param.values = [];
          }
        });
      });
    });
  }

  public getParameterValidation(value: string, parameterType?: number, parameterTypeReference?: string): string {
    if (value === '') return '';
    if (parameterType === undefined && parameterTypeReference !== undefined) {
      parameterType = this.allParameters.find((p) => p.id === parameterTypeReference)?.parameterType;
    }
    const newFormControl = new FormControl<string>(value, [parameterValueTypeValidator(parameterType ?? 0)]);
    return newFormControl.errors ? getParameterTypeErrorMessage(newFormControl) : '';
  }

  public getFinalValues(existingValues: string[] | undefined, defaultValues: string[] | undefined): string[] {
    return existingValues && existingValues.length > 0 ? existingValues : defaultValues || [];
  }
  public getProcessName(processId: number): string {
    const process = this.allProcesses.find((p) => p.processId === processId);
    return process ? process.name.en : '';
  }
  public getParameter(processId: number | null = null, stepId: number | null, parameterRef: string): InputParameter | null {
    if (stepId !== null) {
      const step = this.allSteps.find((s) => s.stepId === stepId);
      if (!step) return null;
      const param = step.additionalParameters.find((s) => s.parameterRef === parameterRef) || null;
      if (!param) return null;
      param.parameter = this.allParameters.find((p) => p.id === param?.parameterRef);
      return param;
    } else if (processId !== null) {
      const process = this.allProcesses.find((p) => p.processId === processId);
      if (!process) return null;
      const param = process.additionalParameters.find((s) => s.parameterRef === parameterRef) || null;
      if (!param) return null;
      param.parameter = this.allParameters.find((p) => p.id === param?.parameterRef);
      return param;
    }
    return null;
  }
  public openStepValuesModal(processId: number, stepId: number | null = null, paramId: string): void {
    const process = this.processes.find((p) => p.processId === processId);

    let parameter: ParameterAndValues | null = null;
    const parameterType = this.allParameters.find((p) => p.id === paramId)?.parameterType;

    if (stepId) {
      const step = process?.stepWithParameters.find((s) => s.stepId === stepId);
      parameter = step?.parameterAndValues.find((p) => p.parameterRef === paramId) || null;
    } else {
      parameter = process?.processParameters.find((p) => p.parameterRef === paramId) || null;
    }

    const formArray: FormArray = new FormArray(
      parameter?.values.map((value) => new FormControl(value, [parameterValueTypeValidator(parameterType ?? 0)])) || [],
    );

    this.modalService
      .open(ParameterValuesModalComponent, {}, { stepValues: formArray, parameterType: parameterType })
      .subscribe((result) => {
        if (result && result.action === 'save' && result.data) {
          parameter!.values = result.data.controls.map((control: FormControl) => control.value);

          console.log('Updated additional parameters:', result.data);
        }
      });
  }

  onSubmit() {
    console.log('onsubmit');
  }
  // public onSubmit(): void {
  //   if (this.formGroup.valid) {
  //     const updatedLabDip: LabDip = {
  //       ...this.labDip,
  //       name: this.name.value!,
  //       status: this.status.value!,
  //       processRefWithStepValues: this.processes,
  //     };
  //     this.isLoading++;
  //     this.isLoading++;
  //     this.cleanUpEmptySingleValueArrays(updatedLabDip);
  //     this.labDipProxyService.upsertLabDip(updatedLabDip).subscribe({
  //       next: () => {
  //         this.router.navigate(['/lab-dips']);
  //       },
  //       error: () => {
  //         alert('Failed to save Lab Dip');
  //         this.isLoading--;
  //         this.isLoading--;
  //       },
  //     });
  //   }
  // }
  public processTypeAhead = (term: string) => {
    const normalizedTerm = term.toLowerCase();
    if (term === null || term === '') {
      return of([]);
    }
    return of(
      _.sortBy(
        _.filter(this.filteredProcesses, (process) => process.name.en.toLowerCase().includes(normalizedTerm)),
        (process) => process.name.en.toLowerCase().indexOf(normalizedTerm),
        (process) => process,
      ),
    );
  };
  public removeProcess(index: number): void {
    this.processes.splice(index, 1);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public parseColorInformation(data: LabDipRequest): any | null {
    if (!data) return null;
    // waiting for changes into API with the property COLOR
    return {
      id: data.id!,
      customer: data.customerReference,
      name: data.colors[0].name,
      // brand: data.colors[0].pantone,
      color: '#a8dadc',
    };
  }
}
